/**
 * 달 위상 그림자 셸 — cartoon 달의 반달/초승달 효과를 만든다.
 *
 * 달 mesh 와 같은 위치에 살짝 더 큰 (1.01x) 구체를 child 로 두고, 이
 * 구체의 한쪽 반구만 어둡게 렌더한다. 어두운 쪽은 "uPhaseAngle 에서
 * 계산한 가상 태양 방향" 의 반대 hemisphere 다.
 *
 * uPhaseAngle 가 한 바퀴 도는 동안 가상 태양 방향이
 *   (cos α, 0, sin α)
 * 로 움직이므로, 카메라(+z 방향) 에서 봤을 때:
 *   α = 0     → 태양 +x → 옆에서 빛 → 반달
 *   α = π/2   → 태양 +z → 카메라 뒤에서 빛 → 보름
 *   α = π     → 태양 -x → 반대쪽 옆에서 → 반달
 *   α = 3π/2  → 태양 -z → 카메라 정면에서 비춤 → 그믐
 * 으로 한 궤도에 위상 한 사이클이 자연스럽게 돈다.
 *
 * 셸은 BackSide 가 아닌 FrontSide 로, 어두운 쪽(달의 그림자) 만 칠한다.
 * 달 surface mesh 는 그대로 보이고, 그림자 영역에서만 어두운 쉘이
 * 위에 덮어 cartoon 위상 효과 완성.
 */

import {
  Color,
  FrontSide,
  Mesh,
  ShaderMaterial,
  SphereGeometry,
} from "three";

const VERT = /* glsl */ `
  varying vec3 vWorldNormal;
  void main() {
    // World-space normal — orientation of the fragment on the sphere.
    vWorldNormal = normalize(mat3(modelMatrix) * normal);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const FRAG = /* glsl */ `
  uniform vec3 uShadowColor;
  uniform float uPhaseAngle;
  /** Soft band width (in cosine-units) around the terminator. Cel feel. */
  uniform float uTerminatorSoft;
  varying vec3 vWorldNormal;
  void main() {
    // Phase-sun direction rotates in the XZ plane as uPhaseAngle advances.
    // Y-axis stays neutral so the orbital plane (XY) yields clean phase
    // cycling viewed from the camera (which is on +z).
    vec3 sunDir = vec3(cos(uPhaseAngle), 0.0, sin(uPhaseAngle));
    float lit = dot(normalize(vWorldNormal), sunDir);
    // lit > +threshold → fully lit (transparent shadow shell).
    // lit < -threshold → fully shadowed (opaque dark).
    // Between → cel terminator step (we keep it hard for cartoon feel).
    if (lit > -uTerminatorSoft) discard;
    gl_FragColor = vec4(uShadowColor, 1.0);
  }
`;

export interface MoonPhaseShellOptions {
  /** Outer radius of the shell — slightly bigger than the moon mesh. */
  radius: number;
  /** Shadow color (default near-black). */
  shadowColor?: number;
}

export interface MoonPhaseShell {
  mesh: Mesh;
  /** Set the orbit angle (radians). Drives phase position. */
  setPhaseAngle(angleRadians: number): void;
  detach(): void;
}

const DEFAULT_SHADOW_COLOR = 0x06080f;

export function createMoonPhaseShell(
  opts: MoonPhaseShellOptions,
): MoonPhaseShell {
  const geometry = new SphereGeometry(opts.radius, 48, 36);
  const material = new ShaderMaterial({
    uniforms: {
      uShadowColor: { value: new Color(opts.shadowColor ?? DEFAULT_SHADOW_COLOR) },
      uPhaseAngle: { value: 0 },
      uTerminatorSoft: { value: 0.04 },
    },
    vertexShader: VERT,
    fragmentShader: FRAG,
    transparent: true,
    side: FrontSide,
    depthWrite: false,
  });
  const mesh = new Mesh(geometry, material);
  mesh.name = "moon-phase-shell";
  // Render the shadow shell after the moon surface so the discarded
  // (lit) fragments don't punch a hole in the underlying mesh.
  mesh.renderOrder = 1;

  const setPhaseAngle = (angleRadians: number): void => {
    material.uniforms.uPhaseAngle!.value = angleRadians;
  };

  const detach = (): void => {
    geometry.dispose();
    material.dispose();
    mesh.removeFromParent();
  };

  return { mesh, setPhaseAngle, detach };
}

export const __testing = { VERT, FRAG, DEFAULT_SHADOW_COLOR };
